import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { finalize, take } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

import { AppState } from '../../../../core/reducers';
import { currentAuthToken } from '../../../../core/auth';
import { TaskService } from '../../../../core/timeline';
import { ActionExecutionStatus } from '../../../../core/enums';
import { LookupType, TransactionType } from '../../forms-templates/enums';
import { FormUtility, LookupFilterEntities } from '../../forms-templates/utils';
import { FormService } from '../../forms-templates/services';
import { MedReviewService } from '../../med-review/services/med-review.service';
import { GeneralAutocompleteComponent } from '../../../partials/layout/general-autocomplete/general-autocomplete.component';

import { AcceptanceResult, Cause, Outcome, TaskStepActionExecutionLog } from '../models';
import {
  TaskEntityType,
  TaskInterventionLevelType,
  TaskInterventionLevelTypeClass,
  TaskVariableAreaView,
} from '../enums';
import { FormioComponent } from 'angular-formio';

@Component({
  selector: 'kt-task-variable-area-view',
  templateUrl: './task-variable-area-view.component.html',
  styleUrls: ['./task-variable-area-view.component.scss'],
})
export class TaskVariableAreaViewComponent implements OnInit, OnDestroy {
  // for HTML template use
  VarAreaView = TaskVariableAreaView;
  actionExecutionStatusClass: Record<ActionExecutionStatus, string> = {
    [ActionExecutionStatus.PENDING]: 'label-outline-dark',
    [ActionExecutionStatus.PROCESSING]: 'label-outline-primary',
    [ActionExecutionStatus.RETRYING]: 'label-outline-warning',
    [ActionExecutionStatus.FAILED]: 'label-outline-danger',
    [ActionExecutionStatus.SUCCESS]: 'label-outline-success',
  };
  //
  private subscriptions = new Subscription();
  private _data: { view: TaskVariableAreaView; disabled: boolean; content: any };
  token: string;
  title: string;
  form;
  formSub: Subscription;
  showComments: boolean;
  actionLogs: TaskStepActionExecutionLog[] = [];
  actionLogsLoading = false;
  actionLogsLoadFailed = false;
  retryingActionLogId: number | null = null;
  getCausesAutocomplete$: Observable<Cause[]>;
  getAcceptanceAutocomplete$: Observable<AcceptanceResult>;
  getOutcomeAutocomplete$: Observable<Outcome>;
  @ViewChild('causesAutocomplete') causesAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('acceptanceAutocomplete') acceptanceAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('outcomeAutocomplete') outcomeAutocomplete: GeneralAutocompleteComponent;
  @Input()
  get data(): { view: TaskVariableAreaView; disabled: boolean; content: any } {
    return this._data;
  }
  set data(value: { view: TaskVariableAreaView; disabled: boolean; content: any }) {
    this._data = value;
    this.handleDataChange(value);
  }
  @Output() inputChange: EventEmitter<{ view: TaskVariableAreaView; content: any }> =
    new EventEmitter();
  @ViewChild('formioComponent') formioComponent: FormioComponent;

  constructor(
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private formService: FormService,
    private formUtility: FormUtility,
    private taskService: TaskService,
    private medReviewService: MedReviewService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.pipe(select(currentAuthToken)).subscribe((token) => (this.token = token)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handleDataChange(data: { view: TaskVariableAreaView; disabled: boolean; content: any }): void {
    this.title = '';
    this.form = null;
    this.showComments = false;
    this.actionLogs = [];
    this.actionLogsLoading = false;
    this.actionLogsLoadFailed = false;
    this.retryingActionLogId = null;

    if (this.formSub) {
      this.formSub.unsubscribe();
      this.formSub = null;
    }

    if (data.view === TaskVariableAreaView.CHECK_LIST) {
      const completedByIds = [
        ...new Set(data.content?.filter((i) => !!i.completed_by_id).map((i) => i.completed_by_id)),
      ];

      if (completedByIds.length) {
        this.taskService
          .getUsersbyId(completedByIds)
          .pipe(take(1))
          .subscribe((res) => {
            if (res?.pageOfItems?.length)
              this.form = data.content?.map((i) => {
                if (!i.completed_by_id) return i;

                return {
                  ...i,
                  completed_by_user: res.pageOfItems.find((u) => u.id === i.completed_by_id),
                };
              });
          });
      } else {
        this.form = data.content?.length ? [...data.content] : null;
      }
    }

    if (data.view === TaskVariableAreaView.INTERVENTION) {
      if (data.content?.drp.drug_code)
        this.medReviewService
          .getDrugByNDC([data.content.drp.drug_code])
          .pipe(take(1))
          .subscribe((res) => {
            if (res?.data?.length) this.title = res.data[0]?.drug || '';
          });

      this.getCausesAutocomplete$ = this.medReviewService.getCauseAutocomplete.bind(
        this.medReviewService,
        data.content?.drp.problem_code,
      );
      this.getAcceptanceAutocomplete$ = this.medReviewService.getAcceptanceAutocomplete.bind(
        this.medReviewService,
      );
      this.getOutcomeAutocomplete$ = this.medReviewService.getOutcomesAutocomplete.bind(
        this.medReviewService,
      );

      this.form = this.formBuilder.group({
        causes: [data.content?.drp.causes],
        interventionNote: [data.content?.intervention_note],
        acceptanceResult: [data.content?.acceptance_result],
        outcome: [data.content?.drp.outcome],
        outcomeNote: [data.content?.drp.outcome_note],
      });

      this.formSub = (this.form as FormGroup).valueChanges.subscribe((res) => {
        const newContent = {
          ...this.data.content,
          drp: {
            ...this.data.content.drp,
            causes: res.causes,
            outcome: res.outcome,
            outcome_note: res.outcomeNote,
          },
          intervention_note: res.interventionNote,
          acceptance_result: res.acceptanceResult,
        };

        this.inputChange.emit({ view: this.data.view, content: newContent });
      });

      if (data.disabled)
        Object.keys(this.form.controls).forEach((key) => this.form.get(key).disable());

      setTimeout(() => {
        this.causesAutocomplete?.setValue(data.content?.drp.causes);
        this.causesAutocomplete?.updateValueAndValidity();
        this.acceptanceAutocomplete?.setValue(data.content?.acceptance_result);
        this.acceptanceAutocomplete?.updateValueAndValidity();
        this.outcomeAutocomplete?.setValue(data.content?.drp.outcome);
        this.outcomeAutocomplete?.updateValueAndValidity();
      }, 100);
    }

    if (data.view === TaskVariableAreaView.FORM) {
      this.formService
        .getFormById(
          data.content.transactionForm?.form.id,
          true,
          data.content.transactionForm?.form_version,
        )
        .pipe(take(1))
        .subscribe((res) => {
          if (!res?.data) return;

          this.title = res.data.name || '';
          this.formSub = data.content.entities$.subscribe((entities) => {
            const filterEntities: LookupFilterEntities = {
              [LookupType.PATIENT]: entities
                .filter((e) => e.entity_type === TaskEntityType.PATIENT)
                .map((e) => e.entity_id),
              [LookupType.DOCTOR]: entities
                .filter((e) => e.entity_type === TaskEntityType.DOCTOR)
                .map((e) => e.entity_id),
              [LookupType.FACILITY]: entities
                .filter((e) => e.entity_type === TaskEntityType.FACILITY)
                .map((e) => e.entity_id),
            };

            this.form = {
              schema: JSON.parse(
                this.formUtility.getSchemaKeysValue(
                  res.data,
                  this.token,
                  TransactionType.TASK,
                  filterEntities,
                ),
              ),
              input: { data: JSON.parse(data.content.transactionForm.input_data || '{}') },
              version: data.content.transactionForm.form_version || res.data.version,
            };
          });
        });
    }

    if (data.view === TaskVariableAreaView.ACTION_EXECUTION_LOGS) {
      this.loadActionLogs();
    }

    if (data.view === TaskVariableAreaView.COMMENTS) {
      if (data.content?.taskId) this.showComments = true;
    }
  }

  loadActionLogs(): void {
    const taskStepId = this.data?.content?.stepId;

    if (!taskStepId) return;

    this.actionLogsLoading = true;
    this.actionLogsLoadFailed = false;
    this.taskService.getTaskStepActionExecutionLogs(taskStepId)
      .pipe(
        take(1),
        finalize(() => {
          this.title = '';
          this.actionLogsLoading = false;
        }),
      )
      .subscribe(
        (res) => (this.actionLogs = res?.data ?? []),
        () => {
          this.actionLogs = [];
          this.actionLogsLoadFailed = true;
        },
      );
  }

  retryAction(log: TaskStepActionExecutionLog): void {
    if (!this.canRetryAction(log?.status) || this.retryingActionLogId !== null) return;

    this.retryingActionLogId = log.id;
    this.taskService.retryTaskStepActionExecution(log.id)
      .pipe(
        take(1),
        finalize(() => (this.retryingActionLogId = null)),
      )
      .subscribe((updated) => {
        this.actionLogs = this.actionLogs.map((log) => log.id === updated.id ? updated : log);
      });
  }

  canRetryAction(status: ActionExecutionStatus): boolean {
    return status === ActionExecutionStatus.SUCCESS || status === ActionExecutionStatus.FAILED;
  }

  handleChecklistChange(index: number): void {
    this.form[index].completed = !this.form[index].completed;
    this.form = this.form.map(({ ...props }) => props);
    this.inputChange.emit({ view: this.data.view, content: this.form });
  }

  handleFormChange(data: any): void {
    if (!data) return;
    this.taskService.setFormValidity(this.formioComponent.formio.checkValidity());
    const newContent = {
      ...this.data.content.transactionForm,
      form: { ...this.data.content.transactionForm.form, schema: JSON.stringify(this.form.schema) },
      form_version: this.form.version,
      input_data: JSON.stringify(data),
    };

    this.inputChange.emit({ view: this.data.view, content: newContent });
  }

  getLevelClass(level: TaskInterventionLevelType): string {
    return level ? `label-light-${TaskInterventionLevelTypeClass[level]}` : '';
  }

  formatCauseOption(cause: Cause): string {
    return cause?.id ? cause.description : '';
  }

  formatNewCause(cause: Cause): Cause {
    return cause;
  }

  formatAcceptanceOption(acceptance: AcceptanceResult): string {
    return acceptance?.id ? acceptance.implementation : '';
  }

  formatOutcomeOption(outcome: Outcome): string {
    return outcome?.id ? outcome.outcome : '';
  }

  selectCauses(value) {
    this.form.get('causes').setValue(value);
  }

  selectAcceptance(value) {
    this.form.get('acceptanceResult').setValue(value);
  }

  selectOutcome(value) {
    this.form.get('outcome').setValue(value);
  }
}
